/**
 * Spotify Web Playback SDK Hook
 * Manages the Spotify player instance and playback state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getRandomAnimalName } from '../utils/animalNames';
import { SnippetController } from '../utils/snippetTimer';
import type { SpotifyPlayer, SpotifyTrackInfo, PlaylistSearchResult } from '../types/spotify.d';

interface UseSpotifyPlayerReturn {
  player: SpotifyPlayer | null;
  deviceId: string | null;
  isReady: boolean;
  isPaused: boolean;
  currentTrack: SpotifyTrackInfo | null;
  position: number;
  duration: number;
  playerName: string;
  playTrack: (trackUri: string) => Promise<void>;
  togglePlay: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  seek: (positionMs: number) => void;
  setVolume: (volume: number) => void;
  playNextAndPause: () => void;
  replayAndPause: () => void;
  searchPlaylists: (query: string) => Promise<PlaylistSearchResult[]>;
  playPlaylist: (playlistUri: string, shuffle?: boolean) => Promise<void>;
}

const PLAYER_REQUEST_RETRIES = 2;
const RETRY_FALLBACK_MS = 1000;
const RETRY_MAX_MS = 5000;

// The player endpoints answer 429 when rate limited and transient 5xx when the
// device is briefly unreachable. Retry-After is only readable if Spotify exposes
// it over CORS, hence the fallback delay.
async function fetchWithRetry(url: string, init: RequestInit, retries = PLAYER_REQUEST_RETRIES): Promise<Response> {
  const response = await fetch(url, init);
  if (retries === 0 || (response.status !== 429 && response.status < 500)) {
    return response;
  }
  const retryAfterSeconds = Number(response.headers.get('Retry-After'));
  const delayMs = retryAfterSeconds > 0 ? Math.min(retryAfterSeconds * 1000, RETRY_MAX_MS) : RETRY_FALLBACK_MS;
  await new Promise(resolve => setTimeout(resolve, delayMs));
  return fetchWithRetry(url, init, retries - 1);
}

export function useSpotifyPlayer(accessToken: string | null, autoPauseDuration: number = 200): UseSpotifyPlayerReturn {
  const [player, setPlayer] = useState<SpotifyPlayer | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTrack, setCurrentTrack] = useState<SpotifyTrackInfo | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [snippet] = useState(() => new SnippetController());
  const autoPauseDurationRef = useRef(autoPauseDuration);

  // Update the duration ref when it changes
  useEffect(() => {
    autoPauseDurationRef.current = autoPauseDuration;
  }, [autoPauseDuration]);

  // Initialize the Spotify Player
  useEffect(() => {
    if (!accessToken) return;

    let spotifyPlayer: SpotifyPlayer | null = null;

    const initializePlayer = () => {
      const animalName = getRandomAnimalName();
      const name = `En una pecota ${animalName}`;
      setPlayerName(name);

      spotifyPlayer = new window.Spotify.Player({
        name: name,
        getOAuthToken: cb => { cb(accessToken); },
        volume: 0.5
      });

      // Ready
      spotifyPlayer.addListener('ready', async ({ device_id }) => {
        console.log('Ready with Device ID', device_id);
        setDeviceId(device_id);
        setIsReady(true);

        // Transfer playback to this device
        try {
          const response = await fetchWithRetry('https://api.spotify.com/v1/me/player', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              device_ids: [device_id],
              play: false
            })
          });
          if (response.ok || response.status === 204) {
            console.log('Playback transferred to web player');
          } else {
            console.log('Transfer playback returned:', response.status, '- this is normal if no previous playback session');
          }
        } catch (error) {
          console.error('Error transferring playback:', error);
        }
      });

      // Not Ready
      spotifyPlayer.addListener('not_ready', ({ device_id }) => {
        console.log('Device ID has gone offline', device_id);
        setIsReady(false);
      });

      // Player state changed
      spotifyPlayer.addListener('player_state_changed', (state) => {
        if (!state) return;

        setCurrentTrack(state.track_window.current_track);
        setIsPaused(state.paused);
        setPosition(state.position);
        setDuration(state.duration);

        if (spotifyPlayer) {
          snippet.onState(spotifyPlayer, state);
        }
      });

      // Error handling
      spotifyPlayer.addListener('initialization_error', ({ message }) => {
        console.error('Initialization Error:', message);
      });

      spotifyPlayer.addListener('authentication_error', ({ message }) => {
        console.error('Authentication Error:', message);
      });

      spotifyPlayer.addListener('account_error', ({ message }) => {
        console.error('Account Error:', message);
      });

      spotifyPlayer.addListener('playback_error', ({ message }) => {
        console.error('Playback Error:', message);
      });

      // Connect to the player
      spotifyPlayer.connect();

      setPlayer(spotifyPlayer);
    };

    // Load Spotify SDK dynamically
    const loadSpotifySDK = () => {
      // Check if already loaded
      if (window.Spotify) {
        initializePlayer();
        return;
      }

      // Define callback before loading script
      window.onSpotifyWebPlaybackSDKReady = initializePlayer;

      // Check if script is already in the document
      if (!document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://sdk.scdn.co/spotify-player.js';
        script.async = true;
        document.body.appendChild(script);
      }
    };

    loadSpotifySDK();

    // Cleanup
    return () => {
      snippet.cancel();
      if (spotifyPlayer) {
        spotifyPlayer.disconnect();
      }
    };
  }, [accessToken, snippet]);

  // Play a track on this device
  const playTrack = useCallback(async (trackUri: string): Promise<void> => {
    if (!deviceId || !accessToken) {
      console.error('Device not ready or no access token');
      return;
    }

    try {
      await fetchWithRetry(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          uris: [trackUri]
        })
      });
    } catch (error) {
      console.error('Error playing track:', error);
    }
  }, [deviceId, accessToken]);

  // Toggle play/pause
  const togglePlay = useCallback((): void => {
    if (!player) return;
    snippet.cancel();
    player.togglePlay();
  }, [player, snippet]);

  // Next track
  const nextTrack = useCallback((): void => {
    if (!player) return;
    player.nextTrack();
  }, [player]);

  // Previous track
  const previousTrack = useCallback((): void => {
    if (!player) return;
    player.previousTrack();
  }, [player]);

  // Seek to position
  const seek = useCallback((positionMs: number): void => {
    if (!player) return;
    snippet.cancel();
    player.seek(positionMs);
  }, [player, snippet]);

  // Set volume (0-1)
  const setVolume = useCallback((volume: number): void => {
    if (!player) return;
    player.setVolume(volume);
  }, [player]);

  // Play next track and auto-pause after configured duration
  const playNextAndPause = useCallback((): void => {
    if (!player) return;
    snippet.arm(player, autoPauseDurationRef.current, currentTrack?.id ?? null);
    player.nextTrack();
  }, [player, snippet, currentTrack]);

  // Replay current track from beginning and auto-pause after configured duration
  const replayAndPause = useCallback((): void => {
    if (!player) return;
    snippet.arm(player, autoPauseDurationRef.current, null);
    player.seek(0).then(() => {
      player.resume();
    });
  }, [player, snippet]);

  // Search for playlists
  const searchPlaylists = useCallback(async (query: string): Promise<PlaylistSearchResult[]> => {
    if (!accessToken || !query.trim()) {
      return [];
    }

    try {
      const response = await fetch(
        `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=playlist&limit=10`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) {
        console.error('Search failed:', response.status);
        return [];
      }

      const data = await response.json();
      return data.playlists.items
        .filter((playlist: unknown) => playlist !== null)
        .map((playlist: {
          uri: string;
          id: string;
          name: string;
          owner?: { display_name?: string };
          images?: { url: string }[];
          tracks?: { total: number };
        }) => ({
          uri: playlist.uri,
          id: playlist.id,
          name: playlist.name,
          owner: playlist.owner?.display_name || 'Unknown',
          image: playlist.images?.[0]?.url || '',
          trackCount: playlist.tracks?.total || 0,
        }));
    } catch (error) {
      console.error('Error searching playlists:', error);
      return [];
    }
  }, [accessToken]);

  // Play a playlist and start auto-pause on first track
  const playPlaylist = useCallback(async (playlistUri: string, shuffle: boolean = true): Promise<void> => {
    if (!deviceId || !accessToken) {
      console.error('Device not ready or no access token');
      return;
    }

    try {
      // First, ensure this device is active
      await fetchWithRetry('https://api.spotify.com/v1/me/player', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          device_ids: [deviceId],
          play: false
        })
      });

      // Enable shuffle if requested
      if (shuffle) {
        await fetchWithRetry(`https://api.spotify.com/v1/me/player/shuffle?state=true&device_id=${deviceId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
      }

      // Then play the playlist
      if (player) {
        snippet.arm(player, autoPauseDurationRef.current, null);
      }
      const response = await fetchWithRetry(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          context_uri: playlistUri
        })
      });

      if (!response.ok && response.status !== 204) {
        console.error('Play request failed:', response.status);
      }
    } catch (error) {
      console.error('Error playing playlist:', error);
    }
  }, [deviceId, accessToken, player, snippet]);

  return {
    player,
    deviceId,
    isReady,
    isPaused,
    currentTrack,
    position,
    duration,
    playerName,
    playTrack,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    setVolume,
    playNextAndPause,
    replayAndPause,
    searchPlaylists,
    playPlaylist,
  };
}

